import { appAssets } from 'constants/assets';
import React, { useEffect, useMemo } from 'react';
import styles from '../CardWidget/CardWidget.module.scss';
import { Widget, WidgetAction } from 'models/WidgetModel';
import CardTitle from '../CardTitle/CardTitle';
import CardHeaderActions from '../CardHeaderActions/CardHeaderActions';
import DataNotFound from 'components/DataNotFound/DataNotFound';
import LoadingSpinner from 'core-ui/LoadingSpinner/LoadingSpinner';
import { useStore } from 'store/useGlobalStore';
import { useShallow } from 'zustand/react/shallow';
import libraryComponents from 'components';
import { showWarningValidation, warningValidData } from 'utils/validators/validatorDataList';
import DataTimeframe from '../DataTimeframe/DataTimeframe';
import { useWidgetContext } from 'contexts/WidgetProvider';
import { getHierarchyFiltersLabelData, getAdvancedFiltersLabelData } from 'helpers/utilities';
import CardFooterActions from 'components/CardFooterActions/CardFooterActions';
import ExportToImage from '../Actions/ExportToImage/ExportToImage';
import html2canvas from 'html2canvas';
import { isDiyWidgetEngineConfig } from 'utils/normalizeWidgetConfig';
import { useEngineRender } from 'components/DiyWidgetEngine/ChartTypes/useEngineRender';
import EngineRenderBoundary from 'components/DiyWidgetEngine/engine/render/EngineRenderBoundary';
import UnsupportedWidgetPlaceholder from 'components/DiyWidgetEngine/engine/render/UnsupportedWidgetPlaceholder';
import { resolveLegacyAlias } from './resolveLegacyAlias';

interface WidgetToRenderProps {
  removeWidget?: (id: number) => void;
  copyWidget?: (id: number) => void;
  editWidget: (widget: any) => void;
  widget: Widget;
  validateData: (res: any) => boolean;
  response: any;
  index: number;
  loading: boolean;
  error: boolean;
  dataSourceType: any;
  customWidgetConfig: any;
  previewMode: boolean;
  dateFilterModel: any;
}

const WidgetToRender: React.FC<WidgetToRenderProps> = ({
  removeWidget,
  copyWidget,
  editWidget,
  widget,
  validateData,
  response,
  index,
  loading,
  error,
  dataSourceType,
  customWidgetConfig,
  previewMode,
  dateFilterModel,
}) => {
  const { displayName, options, viewName, isCustom } = widget;

  const showCardClass = () => {
    if (loading && widget.alias !== 'DynamicTable') return styles[`no-card`];
    return options?.showCard ? styles[`card`] : styles[`no-card`];
  };
  const DeleteWidget = appAssets.icons.DELETE_ICON;
  const EditWidget = appAssets.icons.EDIT_ICON;
  const CopyWidget = appAssets.icons.COPY_ICON;
  const {
    editing,
    diyMode,
    dataGridFeatureFlag,
    enterpriseGlobalObj,
    engineSlices,
    enterpriseSelectedTrend,
    isDiyWidgetEngineByStore,
    useDiyShowCopyWidgetButton,
    customWidget,
    savedEnterpriseWidgetId,
  } = useStore(
    useShallow((state) => ({
      editing: state.editing,
      diyMode: state.diyMode,
      dataGridFeatureFlag: state.configDashboard.dataGridFeatureFlag,
      enterpriseGlobalObj: state.enterpriseGlobalObj,
      engineSlices: state.engineSlices,
      enterpriseSelectedTrend: state.enterpriseSelectedTrend,
      isDiyWidgetEngineByStore: state.isDiyWidgetEngine,
      useDiyShowCopyWidgetButton: state.configDashboard.useDiyShowCopyWidgetButton,
      customWidget: state.customWidget,
      savedEnterpriseWidgetId: state.savedEnterpriseWidgetId,
    }))
  );
  const metricWidgets = ['IndividualMetric', 'MultipleSummaryMetrics'];

  // Backward compatibility for DynamicTableOld
  widget.alias = resolveLegacyAlias(widget.alias, dataGridFeatureFlag);
  const ruleName = widget?.customWidgetConfig?.rules?.[0]?.ruleName;
  const isDynamicTableWidget =
    widget.alias === 'DynamicTable' || widget.alias === 'DynamicTableOld';
  const shouldForceSwitchLineChartAction =
    isDynamicTableWidget && ruleName === 'OneQuestionOneCalculation';
  const headerActions = shouldForceSwitchLineChartAction
    ? Array.from(new Set([...(options?.actions || []), WidgetAction.SWITCH_LINECHART_TABLE]))
    : options?.actions;
  const { setState } = useWidgetContext();
  const hasTrend = widget.customWidgetConfig?.trend?.length;
  const isTextBlock = widget.alias === 'TextBlock';
  const showSpiner = loading && widget.alias !== 'TextAnalytics';
  const isDiyWidgetEngineByConfig = isDiyWidgetEngineConfig(customWidgetConfig);
  const isWidgetBeingEdited =
    (diyMode || (isDiyWidgetEngineByStore && editing)) &&
    ((customWidget?.id || customWidget?.config?.i) === (widget?.id || widget?.config?.i) ||
      (savedEnterpriseWidgetId && savedEnterpriseWidgetId === (widget?.id || widget?.config?.i)));
  // WHY scoped: `isDiyWidgetEngineByStore` is GLOBAL (the drawer's current selection). Applying it
  // to every widget routed all dashboard charts into the engine renderer (DynamicTable/placeholder)
  // while the drawer was open. Only the widget being built/previewed may inherit it.
  const isDiyWidgetEngine =
    isDiyWidgetEngineByConfig ||
    (isDiyWidgetEngineByStore && (previewMode || Boolean(isWidgetBeingEdited)));
  const engineRender = useEngineRender(customWidgetConfig, isDiyWidgetEngine);
  const engineDescriptor = engineRender.status === 'engine' ? engineRender.descriptor : undefined;
  const ChildComponent =
    libraryComponents[
      engineRender.status === 'engine'
        ? resolveLegacyAlias(engineRender.component, dataGridFeatureFlag)
        : widget.alias
    ];

  const excludeExportToImage: string[] = ['DynamicTableOld', 'DynamicTable'];

  const hideExportToImage = excludeExportToImage.includes(widget.alias);

  const trendArray = useMemo(
    () => (enterpriseSelectedTrend?.trend ? [enterpriseSelectedTrend.trend] : undefined),
    [enterpriseSelectedTrend?.trend]
  );

  // WHY: non-Enterprise subtypes mirror their slice under `engineSlices[id]`; Enterprise keeps
  // its historic `enterpriseGlobalObj`.
  const previewSlice =
    (engineDescriptor && engineSlices?.[engineDescriptor.id]) ?? enterpriseGlobalObj;

  const enterpriseMockResponse = useMemo(
    () => engineDescriptor?.preview?.mockBuilder?.(previewSlice, { trend: trendArray }) ?? null,
    [engineDescriptor, previewSlice, trendArray]
  );

  const showEnterpriseMock = isDiyWidgetEngine && (previewMode || isWidgetBeingEdited);

  const effectiveResponse = showEnterpriseMock ? enterpriseMockResponse : response;

  useEffect(() => {
    setState({
      defaults: {
        ...('defaults' in options ? { ...options.defaults } : {}),
      },
    });
  }, [options]);

  let label = '';
  let hierarchyFiltersLabel = [];
  let advancedFiltersLabel = [];

  if (widget.customWidgetConfig?.filters != null) {
    label =
      dateFilterModel?.displayName +
      ': ' +
      dateFilterModel?.startDate +
      ' - ' +
      dateFilterModel?.endDate;

    // Hierarchy Filters Label
    const selectedHierarchyFilters =
      widget.customWidgetConfig.filters?.currentSelection?.hierarchyFilters;

    // Advanced Filters
    const selectedAdvancedFilters =
      widget.customWidgetConfig.filters?.currentSelection?.advancedFilters;

    hierarchyFiltersLabel = getHierarchyFiltersLabelData(selectedHierarchyFilters);
    advancedFiltersLabel = getAdvancedFiltersLabelData(selectedAdvancedFilters);
  }

  const handleCapture = async (id: any, name = 'chart', isJpeg = false) => {
    try {
      const element = document.getElementById(id);
      if (!(element instanceof HTMLElement)) return;

      // Ensure fonts are loaded (critical for font mismatch)
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }

      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: -window.scrollY,

        // Let html2canvas handle cloning safely
        onclone: (doc) => {
          const cloned = doc.getElementById(id);
          if (!cloned) return;

          cloned.querySelectorAll('svg text').forEach((text) => {
            if (text instanceof SVGTextElement) {
              text.style.fontFamily =
                "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial";
              text.style.fontWeight = '700';
            }
          });

          // Make sure everything is visible
          cloned.style.position = 'relative';
          cloned.style.overflow = 'visible';
          cloned.style.transform = 'none';

          // Disable animations/transitions (prevents iframe mismatch)
          cloned.querySelectorAll('*').forEach((el) => {
            if (el instanceof HTMLElement) {
              el.style.animation = 'none';
              el.style.transition = 'none';
            }
          });
        },
        ignoreElements: (el) =>
          el.classList?.contains('meatball-export-menu') || el.classList?.contains('calendar'),
      });

      const image = isJpeg ? canvas.toDataURL('image/jpeg', 0.9) : canvas.toDataURL('image/png');

      // Download
      const link = document.createElement('a');
      link.href = image;
      link.download = `${name}.${isJpeg ? 'jpeg' : 'png'}`;
      link.click();
    } catch (error) {
      console.error('Error capturing the element:', error);
    }
  };

  const renderChild = () => {
    if (engineRender.status === 'unknown') {
      return <UnsupportedWidgetPlaceholder chartTypeId={engineRender.chartTypeId} />;
    }
    const child = (
      <ChildComponent
        {...widget}
        {...(showEnterpriseMock
          ? {
              payload: {
                ...widget.payload,
                isEnterprise: true,
                isSortable: false,
                IsSortable: false,
                QueriesMetadata: widget.payload?.QueriesMetadata?.length
                  ? widget.payload.QueriesMetadata
                  : [{}],
              },
            }
          : {})}
        id={index}
        response={effectiveResponse}
        loading={loading}
        error={error}
        dataSourceType={dataSourceType}
        previewMode={previewMode}
        widgetId={widget?.id}
      />
    );
    return engineDescriptor ? (
      <EngineRenderBoundary chartTypeId={engineDescriptor.id}>{child}</EngineRenderBoundary>
    ) : (
      child
    );
  };

  return (
    <>
      <div
        className={`${showCardClass()} 
            ${metricWidgets.includes(widget.alias) ? styles[`metric-card`] : ''}`}
        id={widget.id}
      >
        {editing && removeWidget && copyWidget ? (
          <div className={`${styles[`cardOverlay`]} widget-card-overlay`}>
            <div className={styles[`cardActions`]}>
              {(widget?.isCustom && widget.payload?.QueriesMetadata == null) ||
              isDiyWidgetEngine ? (
                <EditWidget className={styles[`cardAction`]} onClick={() => editWidget(widget)} />
              ) : null}
              {(useDiyShowCopyWidgetButton && widget.isCustom) || isDiyWidgetEngine ? (
                <CopyWidget
                  className={styles[`cardAction`]}
                  onClick={() => copyWidget(parseInt(widget.config.i))}
                ></CopyWidget>
              ) : null}
              <DeleteWidget
                className={styles[`cardAction`]}
                onClick={() => removeWidget(parseInt(widget.config.i))}
              />
            </div>
          </div>
        ) : null}
        {options?.showTitle || options?.showActions ? (
          <div className={styles[`cardHeader`]}>
            {options?.showTitle && displayName?.length > 0 ? (
              <CardTitle
                displayName={response?.title ? response?.title : displayName}
                viewName={widget.isCustom ? '' : viewName}
                isMetricWidget={metricWidgets.includes(widget.alias)}
                showWarning={showWarningValidation(
                  previewMode,
                  widget?.isMockData || false,
                  widget.alias
                )}
                warningValidData={warningValidData(response, widget.alias)}
                isActions={options?.showActions}
              />
            ) : null}
            {
              // Do not show the action area if data is loading.
              loading ? null : (
                <div className={styles[`card-info-actions`]}>
                  <CardHeaderActions
                    {...{
                      actions: headerActions,
                      response,
                      ruleName: ruleName ?? undefined,
                    }}
                  />
                  {!hideExportToImage ? (
                    <ExportToImage
                      {...{
                        onHandleCapture: handleCapture,
                        id: widget.id,
                        widgetName: widget.displayName,
                      }}
                    />
                  ) : null}
                  {widget.isCustom &&
                    !isTextBlock &&
                    (hasTrend === 0 ||
                      hasTrend === undefined ||
                      widget.customWidgetConfig?.filters?.currentSelection?.hierarchyFilters ||
                      widget.customWidgetConfig?.filters?.currentSelection?.advancedFilters) && (
                      <DataTimeframe
                        timePeriodType={label}
                        hierarchyFiltersValues={hierarchyFiltersLabel}
                        advancedFiltersValues={advancedFiltersLabel}
                        hasTrend={hasTrend !== 0 ? true : false}
                        hasMedusaQuery={
                          widget.payload?.QueriesMetadata !== null &&
                          widget.payload?.QueriesMetadata !== undefined
                            ? true
                            : false
                        }
                      />
                    )}
                </div>
              )
            }
          </div>
        ) : null}
        {showSpiner ? (
          <LoadingSpinner alias={widget.alias} />
        ) : validateData(effectiveResponse) || customWidgetConfig?.dataSource === 'generic' ? (
          renderChild()
        ) : (
          <DataNotFound />
        )}
        {
          // Do not show any action if loading.
          loading ? null : (
            <CardFooterActions {...{ actions: options?.actions, response, isCustom }} />
          )
        }
      </div>
    </>
  );
};

export default WidgetToRender;
